/**
 * AI Expert Agent — Interactive document analysis with extended thinking.
 * State machine: idle → analyzing → questions → generating → result
 * Uses a 3-step agent pipeline: Classify & Question → Deep Extract → Expert Synthesize
 */

import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import type { ParsedDocument } from './document-parser.js';
import type { AnalysisPhase, AnalysisResult, AnalyzeResponse } from './types.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DocumentChatProps {
  documents: ParsedDocument[];
  onAnalysisApproved: (result: AnalysisResult) => void;
  /** Notifies the parent while the AI is actively analyzing/generating. */
  onBusyChange?: (busy: boolean) => void;
}

const MAX_DOCUMENT_CHARS = 400_000;

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function apiCall<T>(body: Record<string, unknown>): Promise<T> {
  // Step 1: Submit job — returns immediately with jobId
  const submitRes = await fetch('/api/documents/summarize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!submitRes.ok) {
    const data = await submitRes.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(data.error || `Server error (${submitRes.status})`);
  }
  const { jobId } = (await submitRes.json()) as { ok: boolean; jobId: string };
  if (!jobId) throw new Error('Server did not return a job ID');

  // Step 2: Poll for result every 3 seconds
  const POLL_INTERVAL = 3_000;
  const MAX_POLL_TIME = 5 * 60_000; // 5 min hard timeout
  const start = Date.now();

  while (Date.now() - start < MAX_POLL_TIME) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL));
    const pollRes = await fetch(`/api/documents/summarize/status/${jobId}`);
    if (!pollRes.ok) {
      if (pollRes.status === 404) throw new Error('Job expired or not found. Please try again.');
      throw new Error(`Poll error (${pollRes.status})`);
    }
    const pollData = await pollRes.json();
    if (pollData.status === 'processing') continue;
    if (pollData.status === 'done') return pollData as T;
    // status === 'error'
    throw new Error(pollData.error || 'AI processing failed');
  }
  throw new Error('Analysis timed out. Please try again with fewer documents.');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DocumentChat = stateful((props: DocumentChatProps) => {
  const { documents, onAnalysisApproved } = props;

  // State machine
  const [phase, setPhase] = useState<AnalysisPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  // Surface "AI is working" to the parent so the wizard can block navigation
  // until the analysis finishes (otherwise the result is lost on step change).
  const isBusy = phase === 'analyzing' || phase === 'generating';
  useEffect(() => {
    props.onBusyChange?.(isBusy);
  }, [isBusy]);

  // Analyze phase data
  const [analyzeData, setAnalyzeData] = useState<AnalyzeResponse | null>(null);

  // Question answers — keyed by question id
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Result
  const [result, setResult] = useState<AnalysisResult | null>(null);

  // Refinement
  const [feedbackText, setFeedbackText] = useState('');

  // Track doc count for re-analyze
  const [analyzedDocCount, setAnalyzedDocCount] = useState(0);

  // Applied state — after "Übernehmen", AI results collapse
  const [applied, setApplied] = useState(false);

  // Editable result fields
  const [editedName, setEditedName] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState<string | null>(null);

  const hasNewDocuments = documents.length !== analyzedDocCount && analyzedDocCount > 0;

  const buildDocumentPayload = () =>
    documents.map(doc => ({
      fileName: doc.fileName,
      pages: doc.pages.map(p => ({
        pageNumber: p.pageNumber,
        text: p.text,
        images: p.images?.map(img => ({ data: img.data, mediaType: img.mediaType, context: img.context })),
      })),
    }));

  const getTruncationInfo = (): { truncated: boolean; originalChars: number } => {
    const totalChars = documents.reduce((sum, d) => sum + d.fullText.length, 0);
    return { truncated: totalChars > MAX_DOCUMENT_CHARS, originalChars: totalChars };
  };

  // --- Smart Routing: Simple (1-call generate) vs Full Agent (extract→synthesize) ---
  // 'auto' = system decides, 'quick' = force simple, 'deep' = force full-agent
  const [analysisMode, setAnalysisMode] = useState<'auto' | 'quick' | 'deep'>('auto');

  const computeAutoIsSimple = (): boolean => {
    const totalChars = documents.reduce((sum, d) => sum + d.fullText.length, 0);
    const hasPptx = documents.some(d => d.fileName.toLowerCase().endsWith('.pptx'));
    const hasImages = documents.some(d => d.pages.some(p => (p.images?.length ?? 0) > 0));
    return totalChars <= 2_000 && documents.length <= 2 && !hasPptx && !hasImages;
  };

  const autoIsSimple = computeAutoIsSimple();
  const isSimple = analysisMode === 'auto' ? autoIsSimple : analysisMode === 'quick';

  // ----- Step 1: Analyze -----
  const startAnalysis = async () => {
    if (documents.length === 0) return;
    setPhase('analyzing');
    setError(null);
    setAnalyzeData(null);
    setResult(null);
    setAnswers({});
    setEditedName(null);
    setEditedDescription(null);
    setApplied(false);
    setAnalyzedDocCount(documents.length);

    try {
      const resp = await apiCall<{ ok: boolean; phase: string; data: AnalyzeResponse }>({
        documents: buildDocumentPayload(),
        phase: 'analyze',
        isSimple,
      });
      setAnalyzeData(resp.data);
      // Pre-fill the first option for each question
      const defaultAnswers: Record<string, string> = {};
      for (const q of resp.data.questions) {
        defaultAnswers[q.id] = q.options[0] ?? '';
      }
      setAnswers(defaultAnswers);
      setPhase('questions');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setPhase('idle');
    }
  };

  // ----- Step 2+3: Generate -----
  const startGeneration = async (skipQuestions = false) => {
    setPhase('generating');
    setError(null);

    try {
      const answersArray = skipQuestions
        ? []
        : Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer }));

      const resp = await apiCall<{ ok: boolean; phase: string; data: AnalysisResult }>({
        documents: buildDocumentPayload(),
        phase: 'generate',
        isSimple,
        answers: answersArray,
        analyzeResult: analyzeData
          ? {
              productName: analyzeData.productName,
              nameConfidence: analyzeData.nameConfidence,
              classification: analyzeData.classification,
            }
          : undefined,
      });

      setResult(resp.data);
      setEditedName(resp.data.productName);
      setEditedDescription(resp.data.productDescription);
      setPhase('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
      setPhase('questions');
    }
  };

  // ----- Step 4: Refine -----
  const refineResult = async () => {
    const trimmed = feedbackText.trim();
    if (!trimmed || !result) return;
    setPhase('generating');
    setError(null);
    setFeedbackText('');

    try {
      const resp = await apiCall<{ ok: boolean; phase: string; data: AnalysisResult }>({
        documents: buildDocumentPayload(),
        phase: 'refine',
        isSimple,
        previousResult: result,
        userFeedback: trimmed,
      });
      setResult(resp.data);
      setEditedName(resp.data.productName);
      setEditedDescription(resp.data.productDescription);
      setPhase('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refinement failed');
      setPhase('result');
    }
  };

  // ----- Apply result to parent -----
  const applyAll = () => {
    if (!result) return;
    onAnalysisApproved({
      productName: editedName ?? result.productName,
      productDescription: editedDescription ?? result.productDescription,
      talkingPoints: result.talkingPoints,
    });
    setApplied(true);
  };

  const truncInfo = getTruncationInfo();

  // =====================================================================
  // RENDER
  // =====================================================================
  return html`
    <div class="flex flex-col gap-3">
      ${
        truncInfo.truncated
          ? html`
            <div class="alert alert-warning text-sm">
              <span class="icon-[material-symbols--warning] text-lg"></span>
              <span>Document content was truncated from ${(truncInfo.originalChars / 1000).toFixed(0)}k to ${(MAX_DOCUMENT_CHARS / 1000).toFixed(0)}k characters.</span>
            </div>
          `
          : nothing
      }

      <!-- Re-analyze hint when documents changed -->
      ${
        hasNewDocuments && phase !== 'analyzing' && phase !== 'generating'
          ? html`
            <div class="alert alert-info text-sm">
              <span class="icon-[material-symbols--info] text-lg"></span>
              <span>Documents changed (${analyzedDocCount} → ${documents.length}). Re-analyze to include all.</span>
              <button class="btn btn-sm btn-primary" @click=${startAnalysis}>
                <span class="icon-[material-symbols--refresh] text-lg"></span>
                Re-analyze
              </button>
            </div>
          `
          : nothing
      }

      <!-- ============ IDLE STATE ============ -->
      ${
        phase === 'idle'
          ? html`
            <div class="flex flex-col gap-2">
              <!-- Analysis mode selector -->
              <div class="flex items-center gap-2">
                <span class="text-base-content/60 text-xs">Analysis depth:</span>
                <div class="join">
                  <button
                    class="join-item btn btn-xs ${analysisMode === 'auto' ? 'btn-active' : ''}"
                    @click=${() => setAnalysisMode('auto')}
                  >
                    Auto${analysisMode === 'auto' ? (autoIsSimple ? ' (Quick)' : ' (Deep)') : ''}
                  </button>
                  <button
                    class="join-item btn btn-xs ${analysisMode === 'quick' ? 'btn-active' : ''}"
                    @click=${() => setAnalysisMode('quick')}
                  >
                    Quick
                  </button>
                  <button
                    class="join-item btn btn-xs ${analysisMode === 'deep' ? 'btn-active' : ''}"
                    @click=${() => setAnalysisMode('deep')}
                  >
                    Deep
                  </button>
                </div>
                ${
                  analysisMode !== 'auto' && analysisMode === 'quick' && !autoIsSimple
                    ? html`<span class="text-warning text-xs">⚠ Complex documents may lose detail</span>`
                    : nothing
                }
              </div>
              <button class="btn btn-primary" @click=${startAnalysis}>
                <span class="icon-[material-symbols--auto-awesome] text-lg"></span>
                Analyze ${documents.length} document${documents.length === 1 ? '' : 's'} with AI Expert
                ${isSimple ? html`<span class="badge badge-sm badge-ghost ml-1">Quick</span>` : html`<span class="badge badge-sm badge-secondary ml-1">Deep</span>`}
              </button>
            </div>
          `
          : nothing
      }

      <!-- ============ ANALYZING STATE ============ -->
      ${
        phase === 'analyzing'
          ? html`
            <div class="border-base-300 flex flex-col items-center gap-3 rounded-lg border p-6">
              <span class="loading loading-spinner loading-lg text-primary"></span>
              <div class="text-center">
                <p class="text-base-content font-medium">AI Expert is reading your documents…</p>
                <p class="text-base-content/60 text-xs">${isSimple ? 'Quick analysis. This usually takes 15-30 seconds.' : 'Deep analysis with extended thinking. This may take 1-2 minutes.'}</p>
              </div>
            </div>
          `
          : nothing
      }

      <!-- ============ QUESTIONS STATE ============ -->
      ${
        phase === 'questions' && analyzeData
          ? html`
            <div class="flex flex-col gap-4">
              <!-- AI's understanding -->
              <div class="border-primary/20 bg-primary/5 rounded-lg border p-4">
                <div class="mb-2 flex items-center gap-2">
                  <span class="icon-[material-symbols--psychology] text-primary text-xl"></span>
                  <span class="font-medium">AI Expert Analysis</span>
                </div>
                <div class="text-sm">
                  <p class="mb-1"><span class="text-base-content/60">Domain:</span> <span class="font-medium">${analyzeData.classification.domain}</span></p>
                  <p><span class="text-base-content/60">Detected product:</span> <span class="font-medium">${analyzeData.productName}</span></p>
                  ${
                    analyzeData.nameConfidence === 'low' &&
                    analyzeData.alternativeNames &&
                    analyzeData.alternativeNames.length > 0
                      ? html`<p class="text-warning mt-1 flex items-center gap-1">
                        <span class="icon-[material-symbols--warning-rounded] text-sm"></span>
                        <span class="text-xs">Multiple names detected: ${analyzeData.alternativeNames.join(', ')} — please confirm below</span>
                      </p>`
                      : nothing
                  }
                </div>
              </div>

              <!-- Questions -->
              ${
                analyzeData.questions.length > 0
                  ? html`
                    <div class="flex flex-col gap-4">
                      <p class="text-base-content/70 text-sm">A few clarifications to maximize outreach quality:</p>

                      ${analyzeData.questions.map(
                        (q, idx) => html`
                          <div class="flex flex-col gap-1.5">
                            <div class="flex items-start gap-2">
                              <span class="bg-primary text-primary-content mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold">${idx + 1}</span>
                              <div class="flex flex-col gap-0.5">
                                <span class="text-sm font-medium leading-snug">${q.question}</span>
                                <span class="text-base-content/40 text-xs">${q.why}</span>
                              </div>
                            </div>
                            <div class="flex flex-wrap gap-1.5 pl-7">
                              ${q.options.map(
                                opt => html`
                                  <button
                                    class="rounded-full border px-3 py-1 text-xs transition-colors ${answers[q.id] === opt ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-base-300 hover:border-primary/40 text-base-content/70'}"
                                    @click=${() => setAnswers({ ...answers, [q.id]: opt })}
                                  >
                                    ${opt}
                                  </button>
                                `,
                              )}
                            </div>
                            <div class="pl-7">
                              ${TextInput({
                                value:
                                  answers[q.id] && !q.options.includes(answers[q.id] ?? '')
                                    ? (answers[q.id] ?? '')
                                    : '',
                                onChanged: (v: string | null) => {
                                  if (v?.trim()) setAnswers({ ...answers, [q.id]: v.trim() });
                                },
                                placeholder: 'Or type a custom answer…',
                              })}
                            </div>
                          </div>
                        `,
                      )}

                      <div class="flex items-center gap-2 pt-1">
                        <button class="btn btn-primary" @click=${() => startGeneration(false)}>
                          <span class="icon-[material-symbols--auto-awesome] text-lg"></span>
                          Generate Outreach Materials
                        </button>
                        <button class="btn btn-ghost btn-sm text-base-content/50" @click=${() => startGeneration(true)}>
                          Skip questions
                        </button>
                      </div>
                    </div>
                  `
                  : html`
                    <p class="text-base-content/70 text-sm">AI has enough context. Ready to generate.</p>
                    <button class="btn btn-primary" @click=${() => startGeneration(true)}>
                      <span class="icon-[material-symbols--auto-awesome] text-lg"></span>
                      Generate Outreach Materials
                    </button>
                  `
              }
            </div>
          `
          : nothing
      }

      <!-- ============ GENERATING STATE ============ -->
      ${
        phase === 'generating'
          ? html`
            <div class="border-base-300 flex flex-col items-center gap-3 rounded-lg border p-6">
              <span class="loading loading-spinner loading-lg text-primary"></span>
              <div class="text-center">
                <p class="text-base-content font-medium">Generating expert outreach materials…</p>
                <p class="text-base-content/60 text-xs">${isSimple ? 'Synthesizing outreach materials. This usually takes 30-60 seconds.' : 'Deep extraction + synthesis with extended thinking. This may take 1-3 minutes.'}</p>
              </div>
              ${
                isSimple
                  ? nothing
                  : html`
              <div class="flex gap-6 text-xs">
                <span class="text-base-content/40">Step 1: Deep extraction</span>
                <span class="text-base-content/40">→</span>
                <span class="text-base-content/40">Step 2: Expert synthesis</span>
              </div>
              `
              }
            </div>
          `
          : nothing
      }

      <!-- ============ RESULT STATE (applied) ============ -->
      ${
        phase === 'result' && result && applied
          ? html`
            <div class="alert alert-success text-sm">
              <span class="icon-[material-symbols--check-circle] text-lg"></span>
              <span>AI results applied to the fields below.</span>
              <button class="btn btn-ghost btn-xs" @click=${() => setApplied(false)}>Show results</button>
            </div>
          `
          : nothing
      }

      <!-- ============ RESULT STATE (not yet applied) ============ -->
      ${
        phase === 'result' && result && !applied
          ? html`
            <div class="flex flex-col gap-4">
              <!-- Product Name -->
              <div class="border-base-300 rounded-lg border p-3">
                <div class="mb-1 flex items-center gap-2">
                  <span class="icon-[material-symbols--label] text-primary text-lg"></span>
                  <span class="text-sm font-medium">Product Name</span>
                </div>
                ${TextInput({
                  value: editedName ?? result.productName,
                  onChanged: (v: string | null) => setEditedName(v ?? result.productName),
                })}
              </div>

              <!-- Product Description -->
              <div class="border-base-300 rounded-lg border p-3">
                <div class="mb-1 flex items-center gap-2">
                  <span class="icon-[material-symbols--description] text-primary text-lg"></span>
                  <span class="text-sm font-medium">Product Description</span>
                  <span class="text-base-content/40 text-xs">(${(editedDescription ?? result.productDescription).split(/\s+/).length} words)</span>
                </div>
                ${TextareaInput({
                  value: editedDescription ?? result.productDescription,
                  onChanged: (v: string | null) => setEditedDescription(v ?? result.productDescription),
                  rows: 5,
                })}
              </div>

              <!-- Talking Points -->
              <div class="border-base-300 rounded-lg border p-3">
                <div class="mb-2 flex items-center gap-2">
                  <span class="icon-[material-symbols--format-list-numbered] text-primary text-lg"></span>
                  <span class="text-sm font-medium">Key Talking Points</span>
                  <span class="badge badge-primary badge-sm">${result.talkingPoints.length}</span>
                </div>
                <div class="flex flex-col gap-1.5">
                  ${result.talkingPoints.map(
                    (point, i) => html`
                      <div class="bg-base-200/50 rounded-lg px-3 py-2 text-sm">
                        <span class="text-primary mr-1 font-medium">${i + 1}.</span> ${point}
                      </div>
                    `,
                  )}
                </div>
              </div>

              <!-- Actions -->
              <div class="flex flex-col gap-2">
                <button class="btn btn-success" @click=${applyAll}>
                  <span class="icon-[material-symbols--check-circle] text-lg"></span>
                  Apply All to Campaign
                </button>

                <div class="flex items-center gap-2">
                  <div class="flex-1">
                    ${TextareaInput({
                      value: feedbackText,
                      onChanged: (v: string | null) => setFeedbackText(v ?? ''),
                      placeholder: 'e.g., "Add more detail about pricing" or "Make talking points more aggressive"',
                      rows: 2,
                    })}
                  </div>
                  <button
                    class="btn btn-outline btn-sm flex-none"
                    ?disabled=${!feedbackText.trim()}
                    @click=${refineResult}
                  >
                    <span class="icon-[material-symbols--refresh] text-lg"></span>
                    Refine
                  </button>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- ============ ERROR STATE (overlay on any phase) ============ -->
      ${
        error
          ? html`
            <div class="alert alert-error text-sm">
              <span class="icon-[material-symbols--error] text-lg"></span>
              <span>${error}</span>
              <button class="btn btn-ghost btn-sm" @click=${startAnalysis}>Retry</button>
            </div>
          `
          : nothing
      }
    </div>
  `;
});
